import { Dispatch, SetStateAction, useRef, useState } from "react";
import {
  getTaskEditorBlockReorderIndex,
  isSameTaskEditorBlockDropPreview,
  reorderBlocks,
  TaskEditorBlock,
  TaskEditorBlockDropPreview,
} from "./taskEditorUtils";

type UseTaskEditorBlockDragArgs = {
  blocks: TaskEditorBlock[];
  setBlocks: Dispatch<SetStateAction<TaskEditorBlock[]>>;
  onBeforeReorder: () => void;
  onReorder: (toIndex: number) => void;
  cancelOtherDrag: () => void;
};

export type UseTaskEditorBlockDragReturn = {
  draggedBlockIndex: number | null;
  blockDropPreview: TaskEditorBlockDropPreview | null;
  handleBlockDragStart: (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number,
    dragImageEl?: HTMLElement | null
  ) => void;
  handleBlockDragOver: (
    event: React.DragEvent<HTMLElement>,
    nextPreview: TaskEditorBlockDropPreview
  ) => void;
  handleBlockDrop: (
    event: React.DragEvent<HTMLElement>,
    preview: TaskEditorBlockDropPreview
  ) => void;
  handleBlockDragEnd: () => void;
  handleBlockCardDragOver: (
    event: React.DragEvent<HTMLElement>,
    targetBlockIndex: number
  ) => void;
  handleBlockCardDrop: (
    event: React.DragEvent<HTMLElement>,
    targetBlockIndex: number
  ) => void;
  cancelDrag: () => void;
};

const DRAG_MIME = "application/task-editor-block";

export function useTaskEditorBlockDrag({
  blocks,
  setBlocks,
  onBeforeReorder,
  onReorder,
  cancelOtherDrag,
}: UseTaskEditorBlockDragArgs): UseTaskEditorBlockDragReturn {
  const [draggedBlockIndex, setDraggedBlockIndex] = useState<number | null>(
    null
  );
  const [blockDropPreview, setBlockDropPreview] =
    useState<TaskEditorBlockDropPreview | null>(null);
  const draggedIndexRef = useRef<number | null>(null);

  const cancelDrag = () => {
    draggedIndexRef.current = null;
    setDraggedBlockIndex(null);
    setBlockDropPreview(null);
  };

  const handleBlockDragStart = (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number,
    dragImageEl?: HTMLElement | null
  ) => {
    if (blockIndex < 0 || blockIndex >= blocks.length) {
      event.preventDefault();
      return;
    }
    cancelOtherDrag();
    draggedIndexRef.current = blockIndex;
    setDraggedBlockIndex(blockIndex);
    setBlockDropPreview(null);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(DRAG_MIME, String(blockIndex));
    // 드래그 고스트를 손잡이 아이콘 대신 블록 카드 전체로 지정해
    // "무엇을 옮기는지" 가 한눈에 보이게 한다.
    if (dragImageEl) {
      event.dataTransfer.setDragImage(dragImageEl, 24, 24);
    }
  };

  // dragover 중에는 배열을 건드리지 않고 삽입 위치 표시만 갱신한다.
  // (실시간 재정렬은 레이아웃이 밀리며 포인터 대상이 바뀌어 진동을 유발)
  // 실제 재정렬은 drop 시점에 1회만 커밋한다.
  const handleBlockDragOver = (
    event: React.DragEvent<HTMLElement>,
    nextPreview: TaskEditorBlockDropPreview
  ) => {
    const source = draggedIndexRef.current ?? draggedBlockIndex;
    if (source === null) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    // 원래 위치 바로 앞/뒤로 떨어뜨리는 건 no-op 이므로 표시선을 숨긴다.
    const targetIndex = getTaskEditorBlockReorderIndex(
      blocks,
      source,
      nextPreview
    );
    if (targetIndex === null || targetIndex === source) {
      setBlockDropPreview((current) => (current === null ? current : null));
      return;
    }

    setBlockDropPreview((current) =>
      isSameTaskEditorBlockDropPreview(current, nextPreview)
        ? current
        : nextPreview
    );
  };

  const getBlockCardDropPreview = (
    targetBlockIndex: number,
    event: React.DragEvent<HTMLElement>
  ): TaskEditorBlockDropPreview | null => {
    const source = draggedIndexRef.current ?? draggedBlockIndex;
    if (source === null || source === targetBlockIndex) return null;
    if (targetBlockIndex < 0 || targetBlockIndex >= blocks.length) return null;
    const rect = event.currentTarget.getBoundingClientRect();
    const pointerY = event.clientY > 0 ? event.clientY : rect.bottom;
    const isBottomHalf = pointerY >= rect.top + rect.height / 2;
    return {
      insertionIndex: targetBlockIndex + (isBottomHalf ? 1 : 0),
    };
  };

  const handleBlockCardDragOver = (
    event: React.DragEvent<HTMLElement>,
    targetBlockIndex: number
  ) => {
    const preview = getBlockCardDropPreview(targetBlockIndex, event);
    if (preview) handleBlockDragOver(event, preview);
  };

  const handleBlockCardDrop = (
    event: React.DragEvent<HTMLElement>,
    targetBlockIndex: number
  ) => {
    const preview = getBlockCardDropPreview(targetBlockIndex, event);
    if (preview) handleBlockDrop(event, preview);
  };

  const handleBlockDrop = (
    event: React.DragEvent<HTMLElement>,
    preview: TaskEditorBlockDropPreview
  ) => {
    event.preventDefault();
    const source = draggedIndexRef.current ?? draggedBlockIndex;
    if (source !== null) {
      const target = getTaskEditorBlockReorderIndex(blocks, source, preview);
      if (target !== null && target !== source) {
        onBeforeReorder();
        setBlocks((currentBlocks) =>
          reorderBlocks(currentBlocks, source, target)
        );
        onReorder(target);
      }
    }
    cancelDrag();
  };

  const handleBlockDragEnd = () => cancelDrag();

  return {
    draggedBlockIndex,
    blockDropPreview,
    handleBlockDragStart,
    handleBlockDragOver,
    handleBlockDrop,
    handleBlockDragEnd,
    handleBlockCardDragOver,
    handleBlockCardDrop,
    cancelDrag,
  };
}
